import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  UserProfile,
} from "firebase/auth";
import {
  onDisconnect,
  onValue,
  ref,
  serverTimestamp,
  set,
} from "firebase/database";
import { auth, db } from "./firebase";
import { ServiceContextType } from "./@types/service";
import { kindOf } from "./lib/selectors";
import { SourceKind } from "./enum";

const fbContext = createContext<ServiceContextType>({} as ServiceContextType);

type Props = {
  children: ReactNode;
};

export const FbProvider: FC<Props> = ({ children }: Props) => {
  const [sources, setSources] = useState<ServiceContextType["sources"]>({});
  const [sourcesReady, setSourcesReady] = useState(false);
  const [state, setState] = useState<ServiceContextType["state"]>({});
  const [rooms, setRooms] = useState<ServiceContextType["rooms"]>({});
  const [devices, setDevices] = useState<ServiceContextType["devices"]>({});

  const local = useState(false);
  const [connected, setConnected] = useState(false);
  const [user, setAuthUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const [isLocal, setLocal] = local;

  const update = useCallback(
    (dat: { [x: string]: number | boolean }) => {
      for (const path in dat) {
        // Split at the FIRST slash: a nested key must not be truncated.
        const cut = path.indexOf("/");
        const addr = cut < 0 ? path : path.slice(0, cut);
        const key = cut < 0 ? "" : path.slice(cut + 1);
        const src = sources[addr];

        // Enforced here as well as in the device form, because this is the one
        // choke point every widget goes through -- and a record configured
        // before its source became telemetry would otherwise keep writing.
        // This is consistency, not security; only RTDB rules stop a determined
        // client.
        if (!key || kindOf(src) === SourceKind.TELEMETRY) continue;

        if (isLocal) {
          // No fallback host: guessing one silently routes a write meant for
          // source B at source A. No ip means no LAN endpoint.
          if (!src?.ip) continue;
          fetch(`http://${src.ip}/set`, {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
            },
            body: `io=${key}&val=${dat[path]}`,
          }).catch((err) => console.log("set-local", err));
        } else if (connected) {
          set(ref(db, path), dat[path]).catch((err) =>
            console.log("fb set:", err)
          );
        }
      }
    },
    [sources, connected, isLocal]
  );

  const signInFn = useCallback(
    (email: string, password: string) =>
      signInWithEmailAndPassword(auth, email, password),
    []
  );
  const signOutFn = useCallback(() => signOut(auth), []);

  const value = useMemo(
    () => ({
      user,
      loading,
      sources,
      sourcesReady,
      state,
      rooms,
      devices,
      local: [isLocal, setLocal] as ServiceContextType["local"],
      connected,
      update,
      signIn: signInFn,
      signOut: signOutFn,
    }),
    [
      user,
      loading,
      sources,
      sourcesReady,
      state,
      rooms,
      devices,
      isLocal,
      setLocal,
      connected,
      update,
      signInFn,
      signOutFn,
    ]
  );

  // A. Auth.
  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      setAuthUser(
        user && {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
        }
      );
      setLoading(false);
    });
  }, []);

  // B. Presence and metadata -- everything whose path is fixed.
  useEffect(() => {
    if (!user) return;

    const userStatusDatabaseRef = ref(db, "/users/" + user.uid);
    const isOfflineForDatabase = {
      user: user.email,
      state: "offline",
      last_changed: serverTimestamp(),
    };
    const isOnlineForDatabase = {
      user: user.email,
      state: "online",
      last_changed: serverTimestamp(),
    };

    const offs = [
      onValue(ref(db, ".info/connected"), (ss) => {
        setConnected(ss.val());
        if (ss.val() === false) return;
        onDisconnect(userStatusDatabaseRef)
          .set(isOfflineForDatabase)
          .then(() => {
            set(userStatusDatabaseRef, isOnlineForDatabase);
          });
      }),
      // `?? {}` everywhere: RTDB hands back null for an empty path.
      onValue(ref(db, "sources"), (ss) => {
        setSources(ss.val() ?? {});
        setSourcesReady(true);
      }),
      onValue(ref(db, "rooms"), (ss) => setRooms(ss.val() ?? {})),
      onValue(ref(db, "devices"), (ss) => setDevices(ss.val() ?? {})),
    ];

    return () => {
      offs.forEach((off) => off());
      // Signing in as a different account must not flash the previous house.
      setSources({});
      setSourcesReady(false);
      setState({});
      setRooms({});
      setDevices({});
    };
  }, [user]);

  /**
   * `ref(db, id)` throws on an illegal key, and this runs inside an effect, so
   * one bad row hand-edited into /sources from the Firebase console would take
   * the whole app down. The form validates on the way in; this guards the way
   * out.
   */
  const usable = (id: string) =>
    !!id && !/[.$#[\]/]/.test(id) && !id.split("").some((c) => c.charCodeAt(0) < 32);

  /**
   * A derived STRING, not the sources object. Editing a label, ip or order
   * mints a new `sources` identity every time, but leaves this primitive
   * untouched -- so effect C does not tear down and reattach every live
   * listener on a metadata edit. Only add, remove or disable moves it.
   */
  const pathKey = useMemo(
    () =>
      Object.keys(sources)
        .filter((a) => usable(a) && !sources[a]?.disabled)
        .sort()
        .join(","),
    [sources]
  );

  // C. One live subscription per enabled source.
  useEffect(() => {
    if (!user) return;
    const addrs = pathKey ? pathKey.split(",") : [];

    setState((prev) => {
      const stale = Object.keys(prev).filter((a) => !addrs.includes(a));
      if (!stale.length) return prev; // identity-stable -> no re-render
      const next = { ...prev };
      stale.forEach((a) => delete next[a]);
      return next;
    });

    // The functional form is mandatory, not stylistic: these callbacks each
    // close over `state`, so `{...state, [a]: v}` would let the last writer win
    // and permanently blank every other tree.
    const offs = addrs.map((a) =>
      onValue(ref(db, a), (ss) =>
        setState((prev) => ({ ...prev, [a]: ss.val() ?? {} }))
      )
    );

    return () => offs.forEach((off) => off());
  }, [user, pathKey]);

  return <fbContext.Provider value={value}>{children}</fbContext.Provider>;
};

export const useFirebase = () => useContext(fbContext);
